import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaBell, FaSignOutAlt, FaComments, FaFileInvoice, FaCalendarAlt, FaClipboardCheck, FaCreditCard, FaFolderOpen, FaCheck, FaExpand, FaCompress } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import Breadcrumbs from "./ui/Breadcrumbs";
import ThemeCustomizer from "./ui/ThemeCustomizer";
import io from "socket.io-client";

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// Notification type icon + color mapping
const getNotifStyle = (type) => {
  switch (type) {
    case "chat":
      return { icon: FaComments, bg: "bg-blue-100 dark:bg-blue-900/40", color: "text-blue-600 dark:text-blue-400" };
    case "invoice":
      return { icon: FaFileInvoice, bg: "bg-emerald-100 dark:bg-emerald-900/40", color: "text-emerald-600 dark:text-emerald-400" };
    case "meeting":
      return { icon: FaCalendarAlt, bg: "bg-purple-100 dark:bg-purple-900/40", color: "text-purple-600 dark:text-purple-400" };
    case "checklist":
      return { icon: FaClipboardCheck, bg: "bg-amber-100 dark:bg-amber-900/40", color: "text-amber-600 dark:text-amber-400" };
    case "payment":
    case "service_purchase":
      return { icon: FaCreditCard, bg: "bg-pink-100 dark:bg-pink-900/40", color: "text-pink-600 dark:text-pink-400" };
    case "document":
      return { icon: FaFolderOpen, bg: "bg-cyan-100 dark:bg-cyan-900/40", color: "text-cyan-600 dark:text-cyan-400" };
    default:
      return { icon: FaBell, bg: "bg-gray-100 dark:bg-dark-700", color: "text-gray-600 dark:text-gray-400" };
  }
};

const AdminHeader = ({ setSidebarOpen }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const toast = useToast();
  const socketRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/admin`);
      if (!response.ok) throw new Error("Failed to fetch notifications.");
      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000); // poll every 15s as fallback
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Socket.io for real-time notification push
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinRoom', 'admin');
    });

    socket.on('newNotification', (notification) => {
      setNotifications(prev => [notification, ...prev]);
    });

    return () => {
      socket.disconnect();
    };
  }, []);
  
  // Fullscreen logic
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('.notification-btn')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = (notif) => {
    setShowDropdown(false);

    // Navigate immediately based on notification link
    if (notif.link) {
      navigate(notif.link);
    }

    // Mark as read in background without awaiting
    const token = localStorage.getItem("adminToken");
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${notif._id}`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
    })
    .then(() => {
      // Remove from list
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    })
    .catch((error) => {
      console.error("Error marking notification as read:", error);
    });
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-all-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ forAdmin: true }),
      });

      setNotifications([]);
      setShowDropdown(false);
      toast.info("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error.message);
      toast.error("Failed to mark notifications as read");
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <header className="sticky top-0 z-30 h-16 bg-gradient-to-r from-black via-dark-900 to-primary-900 text-white dark:from-dark-950 dark:via-dark-900 dark:to-dark-800 backdrop-blur-xl border-b border-white/10 dark:border-dark-700 shadow-lg transition-colors duration-300">
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 rounded-xl text-white hover:bg-white/10 dark:text-gray-300 dark:hover:bg-dark-700 transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars className="w-5 h-5" />
          </button>
          
          <div className="hidden sm:flex items-center text-sm">
            <Breadcrumbs variant="header" />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 md:gap-4 text-white">
          <ThemeCustomizer />

          {/* Full Screen Toggle */}
          <button 
            onClick={toggleFullscreen}
            className="p-2.5 rounded-xl bg-white/10 text-white dark:bg-dark-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-dark-600 transition-all duration-300 group"
            title={isFullscreen ? "Exit Full Screen" : "Enter Full Screen"}
          >
            {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button 
              className="notification-btn relative p-2.5 rounded-xl bg-white/10 text-white dark:bg-dark-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-dark-600 transition-all duration-200"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <FaBell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-full shadow-lg animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showDropdown && (
              <div 
                ref={dropdownRef}
                className="absolute -right-14 sm:right-0 mt-2 w-[300px] sm:w-96 bg-white dark:bg-dark-800 rounded-2xl shadow-3d border border-gray-100 dark:border-dark-700 overflow-hidden animate-fade-in z-50"
              >
                {/* Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Notifications</h3>
                    <p className="text-xs text-white/70">{unreadCount} unread</p>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <FaCheck className="w-3 h-3" />
                      Mark all read
                    </button>
                  )}
                </div>
                
                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 20).map((notif) => {
                      const style = getNotifStyle(notif.type);
                      const IconComp = style.icon;
                      return (
                        <div 
                          key={notif._id} 
                          className="px-4 py-3 border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer flex items-start gap-3"
                          onClick={() => handleNotificationClick(notif)}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.bg}`}>
                            <IconComp className={`w-4 h-4 ${style.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{notif.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo(notif.createdAt)}</p>
                          </div>
                          {!notif.isRead && (
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <FaBell className="w-8 h-8 text-gray-300 dark:text-dark-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No new notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Logout Button */}
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white font-medium shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200"
          >
            <FaSignOutAlt className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;